import { NextPage } from "next"
import Error from "next/error"
import Head from "next/head"
import React from "react"
import styled, { css } from "styled-components"

import Typography from "~/components/atoms/Typography"
import { Container } from "~/components/layouts/Container"
import Breadcrumb from "~/components/organisms/Breadcrumb"
import CompanyHead from "~/components/organisms/CompanyHead"
import CompanyMemberIntroductionItem from "~/components/organisms/CompanyMemberIntroductionItem"
import DetailDescription from "~/components/organisms/DetailDescription"
import DetailMenu from "~/components/organisms/DetailMenu"
import Footer from "~/components/organisms/Footer"
import Header from "~/components/organisms/Header"
import Table from "~/components/organisms/Table"
import { IsPC, IsMobile, SpacingSize, Color, Z } from "~/consts/style-mixins"
import { internalLink } from "~/services/helper"
import { InfraNextPageContext } from "~/types/InfraNextPageContext"
import { ReturnGetInitialProps } from "~/types/ReturnGetInitialProps"

const getInitialProps = async ({ initialStoreData: { companyStore }, query, res }: InfraNextPageContext) => {
  const company = await companyStore.fetchById(Number(query["company-id"]))
  if (!company || !company.id) {
    res.statusCode = 404
  }

  return { company }
}

type GetInitialProps = ReturnGetInitialProps<typeof getInitialProps>

const Page: NextPage<GetInitialProps> = ({ company }) => {
  if (!company || !company.id) {
    return <Error statusCode={404} />
  }

  const crumbs = [
    {
      name: "企業一覧",
      link: internalLink(`/companies`),
    },
    {
      name: `${company.name}のインターン・新卒採用情報`,
      link: internalLink(`/companies/${company.id}`),
    },
  ]

  return (
    <>
      <Head>
        <title>{company.name}のインターン・新卒採用情報 | Infra（インフラ）</title>
        <meta
          name="description"
          content={`Infra（インフラ）では${company.name}の長期インターンシップや短期インターン、説明会 / イベント、新卒採用情報を掲載しています。インターンシップから就職活動まで学生のキャリア構築を一貫してサポートします。`}
        />
        <meta property="og:title" content={`${company.name}のインターン・新卒採用情報 | Infra（インフラ）`} />
        <meta
          property="og:description"
          content={`Infra（インフラ）では${company.name}の長期インターンシップや短期インターン、説明会 / イベント、新卒採用情報を掲載しています。インターンシップから就職活動まで学生のキャリア構築を一貫してサポートします。`}
        />
        <meta property="og:image" content={company.logoImgUrl} />
      </Head>
      <Header logoType="Plain" showTab />
      <Container size="Large">
        <StyledInnerContainer>
          <StyledDetailMenuWrapper>
            <DetailMenu company={company} selectedTabIndex={1} />
          </StyledDetailMenuWrapper>
          <StyledMainContentWrapper>
            <StyledMainContentInner>
              <StyledCompanyHead company={company} />
              {company.mission && (
                <StyledEachContentWrapper>
                  <DetailDescription heading="目指していること" description={company.mission} />
                </StyledEachContentWrapper>
              )}
              {company.overview && (
                <StyledEachContentWrapper>
                  <DetailDescription heading="事業内容" description={company.overview} />
                </StyledEachContentWrapper>
              )}
              {company.culture && (
                <StyledEachContentWrapper>
                  <DetailDescription heading="社風・風土" description={company.culture} />
                </StyledEachContentWrapper>
              )}
              <StyledEachContentWrapper>
                {company.recommendedPoints && (
                  <DetailDescription heading="おすすめポイント" description={company.recommendedPoints} />
                )}
              </StyledEachContentWrapper>
              {company.companyMembers && company.companyMembers.length !== 0 && (
                <StyledEachContentWrapper>
                  <StyledSubtitleTypography type="SubHeading">社員紹介</StyledSubtitleTypography>
                  {company.companyMembers.map((member, index) => (
                    <StyledCompanyMemberIntroductionItem key={index} user={member} />
                  ))}
                </StyledEachContentWrapper>
              )}
              <StyledEachContentWrapper>
                <StyledSubtitleTypography type="SubHeading">会社概要</StyledSubtitleTypography>
                <Table
                  items={[
                    { name: "会社名", content: company.name },
                    { name: "資本金", content: company.capital },
                    { name: "設立日", content: company.establishedOn },
                    {
                      name: "従業員規模",
                      content: company.employeeNumberRanges ? `${company.employeeNumberRanges}名` : null,
                    },
                    {
                      name: "所在地",
                      content: company.prefecture + company.address1 + company.address2 + company.address3,
                    },
                    { name: "会社URL", content: company.siteUrl },
                  ]}
                />
              </StyledEachContentWrapper>
            </StyledMainContentInner>
          </StyledMainContentWrapper>
        </StyledInnerContainer>
      </Container>
      <Breadcrumb crumbs={crumbs} />
      <Footer />
    </>
  )
}

Page.getInitialProps = getInitialProps

export default Page

const StyledInnerContainer = styled.div`
  ${IsPC(css`
    padding: 48px 0;
    display: flex;
    flex-wrap: wrap;
    min-height: calc(100vh - 215px);
  `)}
  ${IsMobile(css`
    padding-bottom: 32px;
    min-height: calc(100vh - 330px);
  `)}
`

const StyledDetailMenuWrapper = styled.div`
  position: sticky;
  z-index: ${Z.Tab};
  ${IsPC(css`
    top: 16px;
    height: 100%;
  `)}
  ${IsMobile(css`
    top: 0;
  `)}
`

const StyledMainContentWrapper = styled.div`
  width: calc(100% - 280px);
  ${IsMobile(css`
    width: 100%;
  `)}
`

const StyledMainContentInner = styled.div`
  ${IsPC(css`
    padding-left: 48px;
  `)}
`

const StyledEachContentWrapper = styled.div`
  &:not(:last-child) {
    margin-bottom: ${SpacingSize["2XL"]}px;
  }
`

const StyledCompanyHead = styled(CompanyHead)`
  margin-bottom: ${SpacingSize.XL}px;
`

const StyledSubtitleTypography = styled(Typography)`
  margin-bottom: ${SpacingSize.M}px;
  color: ${Color.Primary};
`

const StyledCompanyMemberIntroductionItem = styled(CompanyMemberIntroductionItem)`
  &:not(:last-child) {
    margin-bottom: 24px;
  }
`
